import "./styles.css";
import React, { useMemo } from "react";
import {
  FaClock,
  FaExclamationCircle,
  FaMoneyBill,
  FaTruck,
  FaUserAlt,
} from "react-icons/fa";
import { TbDeviceIpadExclamation } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { format, toZonedTime } from "date-fns-tz";
import { addHours } from "date-fns";
import Swal from "sweetalert2";
import api from "../../Services/api";

const ListaOcorrenciaAvaliacao = ({ lista = [], maxHeight, onAtualizarLista }) => {
  const navigate = useNavigate();

  const formatarDataHora = (datahora) => {
    if (!datahora) return "-";
    const timeZone = "America/Sao_Paulo";
    const dataZoned = toZonedTime(datahora, timeZone);
    const dataSomada = addHours(dataZoned, 0);
    return format(dataSomada, "dd/MM/yyyy HH:mm:ss");
  };

  const formatarMoeda = (valor) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(valor || 0));

  const sortedLista = useMemo(() => {
    return [...lista].sort((a, b) => {
      const dataA = a.datahora ? new Date(a.datahora).getTime() : 0;
      const dataB = b.datahora ? new Date(b.datahora).getTime() : 0;
      return dataB - dataA;
    });
  }, [lista]);

  const handleExibir = (idVisita, idUsuario) => {
    navigate(`/Visita/${idUsuario}/${idVisita}`);
  };

  const handleEnviar = async (item) => {
    const confirm = await Swal.fire({
      title: "Confirmar envio?",
      text: `Deseja realmente enviar como OK a ocorrencia da visita n ${item.IDvisita}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, enviar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    const dados = JSON.parse(localStorage.getItem("usuarioLogado"));

    try {
      const payload = {
        IDvisita: item.IDvisita,
        situacao: "1",
        observacao: `Avaliado - ${new Date().toLocaleString("pt-BR")} - ${dados.pessoa.nome}`,
      };

      const response = await api.post("/ocorrencia/avaliacao", payload);

      if (response.status === 201) {
        Swal.fire({
          title: "Sucesso!",
          text: "Ocorrencia avaliada com sucesso.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          if (onAtualizarLista) onAtualizarLista();
        });
        return;
      }

      Swal.fire({
        title: "Atencao",
        text: "Ocorreu um problema ao enviar a avaliacao da ocorrencia.",
        icon: "warning",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Erro",
        text: "Nao foi possivel enviar a avaliacao da ocorrencia.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div
      className="card-oco-container"
      style={{ maxHeight: maxHeight || "75vh", overflowY: "auto" }}
    >
      {sortedLista.map((item) => (
        <div
          key={item.IDvisita}
          className="card-oco"
          onClick={() => handleExibir(item.IDvisita, item.IDusuario)}
        >
          <div className="card-oco-content">
            <div className="card-oco-header">
              <div className="card-oco-title-block">
                <span className="card-oco-badge">Ocorrencia</span>
                <h3 className="card-oco-title">
                  <FaUserAlt />
                  {item.IDusuario} - {item.nome}
                </h3>
                <p className="card-oco-subtitle">
                  Carga n: {item.IDlote} | Visita n: {item.IDvisita} | Cliente: {item.IDcliente} -{" "}
                  {item.cliente}
                </p>
              </div>

              <div className="card-oco-date">
                <FaClock />
                <span>{formatarDataHora(item.datahora)}</span>
              </div>
            </div>

            <div className="card-oco-grid">
              <div className="card-oco-meta">
                <p>
                  <FaMoneyBill />
                  Valor: {formatarMoeda(item.valor)} | Qtde: {item.quantidade}
                </p>
                <p>
                  <FaMoneyBill />
                  NF: {item.notaFiscal} | Pag: {item.planopag}
                </p>
                <p>
                  <FaTruck />
                  Veiculo: {item.modelo} ({item.placa})
                </p>
                <p>
                  <TbDeviceIpadExclamation />
                  Obs: {item.observacao || "-"}
                </p>
              </div>

              <div className="card-oco-details">
                {item.registro?.map((reg, idx) => (
                  <div key={`${item.IDvisita}-${idx}`} className="card-oco-detail-box">
                    <p className="card-oco-detail-title">
                      <FaExclamationCircle />
                      {reg.TipoOcorrencia}
                    </p>
                    <p className="card-oco-detail-text">Obs: {reg.Observacao || "-"}</p>
                    <p className="card-oco-detail-text">Pagto Mercadoria: {!!reg.PagamentoMercadoria ? "Sim" : "Não"}</p>

                    {reg.Produtos?.length > 0 && (
                      <div className="card-oco-products">
                        <span className="card-oco-products-title">Produtos</span>
                        <ul>
                          {reg.Produtos.map((prod) => (
                            <li key={`${reg.TipoOcorrencia}-${prod.IDproduto}`}>
                              <strong>{prod.descricao}</strong>
                              <span>
                                ID: {prod.IDproduto} | Qtde ocorr.: {prod.quantidadeNaOcorrencia}
                              </span>
                              <span>
                                Qtde: {prod.qtde} | Preco unit.: {formatarMoeda(prod.precouni)} |
                                Total: {formatarMoeda(prod.precototal)}
                              </span>
                              {prod.obs ? <span>Obs: {prod.obs}</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="card-oco-actions">
              <button
                type="button"
                className="card-oco-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEnviar(item);
                }}
              >
                OK - Avaliado
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ListaOcorrenciaAvaliacao;
